import { Link } from "react-router-dom";
import { ArrowRight, Heart, ThumbsUp, Radio, MapPin, ListChecks, FileWarning, Zap } from "lucide-react";

const platformFeatures = [
  {
    icon: ThumbsUp, title: "Voting Feed", status: "live", to: "/feed",
    desc: "Real bias scenarios, submitted daily. The community votes on whether it's bias — and how serious it is.",
  },
  {
    icon: Radio, title: "BERN Emergency Response", status: "building", to: "/bern",
    desc: "Live-streamed witness network for violent bias events. Go live, get seen, get help — in real time.",
  },
  {
    icon: MapPin, title: "The BBAP Book", status: "building", to: "/bbap-book",
    desc: "A modern directory of businesses that treat people with dignity — know exactly where you're welcome.",
  },
  {
    icon: ListChecks, title: "Guidelines & SOPs", status: "live", to: "/guidelines",
    desc: "Plain-language steps for during and after a bias event. Know exactly what to do, before you need it.",
  },
  {
    icon: FileWarning, title: "Incident Documentation", status: "live", to: "/report-incident",
    desc: "File a detailed report with evidence. This is the data that fuels our advocacy and lobbying work.",
  },
  {
    icon: Heart, title: "Fund the Movement", status: "live", to: "/donate",
    desc: "One-time or monthly donations power BERN, advocacy, and outreach. Every dollar pushes for change.",
  },
];

function StatusBadge({ status }) {
  if (status === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#4FD1C5]/15 text-[#1E3A6E] border border-[#4FD1C5]/40">
        <span className="h-1.5 w-1.5 rounded-full bg-[#1E3A6E] animate-pulse" /> Live Now
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-zinc-100 text-zinc-500 border border-zinc-300">
      Building Now
    </span>
  );
}

export default function PlatformFeatures() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="flex items-center gap-2 justify-center mb-3">
        <Zap className="h-4 w-4 text-[#1E3A6E]" />
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A6E]">One Platform. Full Power.</p>
      </div>
      <h2 className="font-display font-extrabold uppercase text-3xl md:text-4xl text-zinc-950 tracking-tight text-center mb-4">
        Explore the Platform
      </h2>
      <p className="text-zinc-600 text-center max-w-2xl mx-auto mb-12">
        Every tool BBAP is building toward — some live today, some in active development. Here's the whole picture.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {platformFeatures.map((f) => (
          <Link
            key={f.title} to={f.to} data-testid={`home-feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            className="group relative bg-white border border-zinc-200 rounded-sm p-7 hover:border-zinc-950 hover:shadow-xl transition-all duration-200 overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-full h-0.5 bg-zinc-200 group-hover:bg-[#4FD1C5] transition-colors" />
            <div className="flex items-start justify-between mb-5">
              <div className="h-12 w-12 rounded-full bg-zinc-950 flex items-center justify-center group-hover:bg-[#1E3A6E] transition-colors">
                <f.icon className="h-5 w-5 text-[#4FD1C5]" />
              </div>
              <StatusBadge status={f.status} />
            </div>
            <h3 className="font-display font-bold text-lg text-zinc-950 mb-2">{f.title}</h3>
            <p className="text-zinc-500 text-sm leading-relaxed mb-4">{f.desc}</p>
            <span className="text-[#1E3A6E] text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Explore <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
